import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from 'recharts';
import { useCourse } from '../../contexts/CourseContext';
import { FitnessCenterOutlined, AddOutlined, CloseOutlined, DeleteOutlineOutlined } from '../../icons';
import PlansModal from '../../components/PlansModal';
import TrainingTimeFields from '../../components/TrainingTimeFields';
import { TRAINING_DIARY_LIMIT } from '../../planLimits';
import { TEST_TYPES, parseTrainingTime, formatTrainingTime, formatTrainingResult, formatTrainingRequirement, trainingProtocolKey, evaluateTrainingGoal } from '../../utils/trainingGoal';
import './PhysicalTraining.css';
const apiUrl = process.env.REACT_APP_API_URL;
const today = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const dateLabel = date => new Date(`${String(date).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR', {
  day: '2-digit',
  month: '2-digit'
});
export function PhysicalTrainingLogModal({
  exercises,
  defaultExerciseId,
  onClose,
  onSave,
  existingLog
}) {
  const initial = existingLog?.protocol || exercises.find(ex => String(ex.id) === String(defaultExerciseId)) || exercises[0];
  const [exerciseId, setExerciseId] = useState(String(existingLog?.exercise_id || defaultExerciseId || exercises[0]?.id || ''));
  const [value, setValue] = useState(existingLog ? String(existingLog.result_value) : '');
  const [minutes, setMinutes] = useState(existingLog && initial?.test_type === 'isometric_hold' ? String(Math.floor(existingLog.result_value / 60)) : '');
  const [seconds, setSeconds] = useState(existingLog && initial?.test_type === 'isometric_hold' ? String(existingLog.result_value % 60) : '');
  const [elapsedMinutes, setElapsedMinutes] = useState(existingLog?.elapsed_seconds ? String(Math.floor(existingLog.elapsed_seconds / 60)) : '');
  const [elapsedSeconds, setElapsedSeconds] = useState(existingLog?.elapsed_seconds ? String(existingLog.elapsed_seconds % 60) : '');
  const [performedAt, setPerformedAt] = useState(existingLog ? String(existingLog.performed_at).slice(0, 10) : today());
  const [notes, setNotes] = useState(existingLog?.notes || ''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const exercise = existingLog?.protocol || exercises.find(ex => String(ex.id) === exerciseId);
  const type = TEST_TYPES[exercise?.test_type];
  const submit = async event => {
    event.preventDefault();
    setError('');
    try {
      if (!exercise) throw new Error('Selecione o exercício.');
      const result = exercise.test_type === 'isometric_hold' ? parseTrainingTime(minutes, seconds, {
        allowZero: true
      }) : value === '' ? null : Number(value);
      if (result == null || !Number.isFinite(result) || result < 0) throw new Error('Informe o desempenho realizado.');
      if (exercise.test_type !== 'distance_fixed_time' && !Number.isInteger(result)) throw new Error('Informe repetições ou segundos inteiros.');
      let elapsed = null;
      if (exercise.test_type === 'distance_fixed_time') {
        elapsed = parseTrainingTime(elapsedMinutes, elapsedSeconds);
        if (!elapsed) throw new Error('Informe o tempo gasto na corrida.');
      } else if (type.timed) {
        elapsed = existingLog && !existingLog.is_complete_test ? existingLog.elapsed_seconds : Number(exercise.test_duration_seconds);
      }
      if (!performedAt) throw new Error('Informe a data do treino.');
      setBusy(true);
      await onSave({
        exerciseId: Number(exerciseId),
        resultValue: result,
        isCompleteTest: existingLog ? !!existingLog.is_complete_test : true,
        elapsedSeconds: elapsed,
        performedAt,
        notes: notes.trim()
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.errors?.map(issue => issue.message).join(' ') || e.response?.data?.message || e.message || 'Não foi possível salvar o desempenho.');
    } finally {
      setBusy(false);
    }
  };
  return <div className="physical-training-modal-backdrop" onClick={() => {
    if (!busy) onClose();
  }}>
    <div className="physical-training-modal-card" role="dialog" aria-modal="true" aria-label="Registrar desempenho" onClick={event => event.stopPropagation()}>
      <button className="physical-training-modal-close" type="button" disabled={busy} aria-label="Fechar" onClick={onClose}><CloseOutlined /></button>
      <h2 className="physical-training-modal-title">{existingLog ? 'Editar desempenho' : 'Registrar desempenho'}</h2>
      <form className="physical-training-modal-form" onSubmit={submit}><fieldset className="taf-form-fields" disabled={busy}>
        <label className="taf-field">Exercício<select aria-label="Exercício" disabled={!!existingLog} value={exerciseId} onChange={event => {
              setExerciseId(event.target.value);
              setValue('');
              setMinutes('');
              setSeconds('');
              setElapsedMinutes('');
              setElapsedSeconds('');
              setError('');
            }}>{exercises.map(ex => <option key={ex.id} value={ex.id}>{ex.name}</option>)}</select></label>
        {exercise && <div className="taf-requirement"><small>{existingLog ? 'Exigência na data do registro' : 'Meta do edital'}</small><strong>{formatTrainingRequirement(exercise)}</strong>{exercise.instructions && <p>{exercise.instructions}</p>}</div>}
        {type && <>
          {exercise.test_type === 'isometric_hold' ? <TrainingTimeFields prefix="Tempo sustentado" minutes={minutes} seconds={seconds} onMinutes={setMinutes} onSeconds={setSeconds} /> : <label className="taf-field">{type.field}<input type="number" min="0" max="99999999.99" step={exercise.test_type === 'distance_fixed_time' ? '0.01' : '1'} value={value} onChange={e => setValue(e.target.value)} required /></label>}
          {exercise.test_type === 'distance_fixed_time' && <div className="taf-running-time"><TrainingTimeFields prefix="Tempo gasto" minutes={elapsedMinutes} seconds={elapsedSeconds} onMinutes={setElapsedMinutes} onSeconds={setElapsedSeconds} /><p>Informe a distância que percorreu e quanto tempo levou. A meta exige a distância mínima dentro do tempo limite.</p></div>}
        </>}
        <label className="taf-field">Data<input type="date" value={performedAt} onChange={e => setPerformedAt(e.target.value)} required /></label>
        <label className="taf-field">Observações (opcional)<textarea rows={2} maxLength={500} value={notes} onChange={e => setNotes(e.target.value)} /></label>
        {error && <p role="alert" className="physical-training-modal-error">{error}</p>}
        <button type="submit" className="physical-training-submit-button">{busy ? 'Salvando…' : 'Salvar desempenho'}</button>
      </fieldset></form>
    </div>
  </div>;
}
export default function PhysicalTrainingPanel() {
  const {
    activeCourseId,
    activeCourse,
    loadingCourses
  } = useCourse();
  const [exercises, setExercises] = useState([]),
    [logs, setLogs] = useState([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null),
    [modal, setModal] = useState(null),
    [hasFreePlan, setHasFreePlan] = useState(false),
    [paywall, setPaywall] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (loadingCourses || !activeCourseId) {
      setExercises([]);
      setLogs([]);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setError('');
    setModal(null);
    setExpanded(null);
    Promise.all([axios.get(`${apiUrl}physicalExercises`, {
      params: {
        courseId: activeCourseId
      }
    }), axios.get(`${apiUrl}physicalTrainingLogs`, {
      params: {
        courseId: activeCourseId
      }
    })]).then(([ex, history]) => {
      if (alive) {
        setExercises(ex.data.exercises || []);
        setLogs(history.data.logs || []);
        setHasFreePlan(!!history.data.hasFreePlan);
      }
    }).catch(() => {
      if (alive) setError('Não foi possível carregar o treino físico.');
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [activeCourseId, loadingCourses, revision]);
  const groups = useMemo(() => {
    const map = {};
    logs.forEach(log => {
      if (!map[log.exercise_id]) map[log.exercise_id] = [];
      map[log.exercise_id].push(log);
    });
    return map;
  }, [logs]);
  const currentLogs = useCallback(exercise => (groups[exercise.id] || []).filter(log => trainingProtocolKey(log.protocol) === trainingProtocolKey(exercise)), [groups]);
  const achieved = exercises.filter(ex => evaluateTrainingGoal(currentLogs(ex).find(log => log.is_complete_test), ex).status === 'achieved').length;
  const month = today().slice(0, 7);
  const days = new Set(logs.filter(log => String(log.performed_at).startsWith(month)).map(log => String(log.performed_at).slice(0, 10))).size;
  const open = exerciseId => {
    if (hasFreePlan && logs.length >= TRAINING_DIARY_LIMIT) {
      setPaywall(true);
      return;
    }
    setModal({
      exerciseId
    });
  };
  const save = async data => {
    try {
      if (modal.log) {
        const {
          exerciseId,
          ...update
        } = data;
        await axios.put(`${apiUrl}physicalTrainingLogs/${modal.log.id}`, update);
      } else await axios.post(`${apiUrl}physicalTrainingLogs`, data);
      setRevision(r => r + 1);
    } catch (e) {
      if (e.response?.data?.code === 'PLAN_LIMIT_REACHED') {
        setModal(null);
        setPaywall(true);
        return;
      }
      throw e;
    }
  };
  const remove = async id => {
    if (!window.confirm('Excluir este registro de desempenho?')) return;
    try {
      await axios.delete(`${apiUrl}physicalTrainingLogs/${id}`);
      setLogs(rows => rows.filter(log => log.id !== id));
    } catch {
      setError('Não foi possível excluir o registro.');
    }
  };
  return <section className="physical-training-panel">
    <h2 className="physical-training-title"><FitnessCenterOutlined /> Treino físico</h2>
    <p className="physical-training-subtitle">Registre seu desempenho conforme as exigências do TAF de {activeCourse?.name || 'seu concurso'}.</p>
    {error && <p role="alert">{error}</p>}
    {loading || loadingCourses ? <p role="status">Carregando treino físico…</p> : !activeCourseId ? <p>Selecione um concurso para acompanhar o TAF.</p> : <>
      <div className="kpi-strip physical-training-kpi-strip"><div className="kpi-card"><span className="kpi-label">DIAS TREINADOS NO MÊS</span><span className="kpi-value">{days}</span></div><div className="kpi-card kpi-card--good"><span className="kpi-label">METAS ATINGIDAS</span><span className="kpi-value">{achieved} / {exercises.length}</span><small>Último teste completo de cada exigência atual</small></div><div className="kpi-card"><span className="kpi-label">DESEMPENHOS REGISTRADOS</span><span className="kpi-value">{logs.length}</span></div></div>
      <div className="physical-training-section-header"><span className="physical-training-section-title">Exigências do edital</span><button type="button" className="physical-training-add-button" disabled={!exercises.length} onClick={() => open()}><AddOutlined /> Registrar desempenho</button></div>
      {!exercises.length && <p>Nenhum exercício disponível para seu concurso e público.</p>}
      <div className="physical-training-exercise-list">{exercises.map(ex => {
          const history = groups[ex.id] || [],
            compatible = currentLogs(ex),
            last = compatible[0];
          const outcome = evaluateTrainingGoal(last, ex),
            percent = Math.max(0, Math.min(100, outcome.ratio * 100));
          const graph = [...compatible].filter(log => log.is_complete_test && (ex.test_type !== 'distance_fixed_time' || Number(log.result_value) >= Number(ex.minimum_result))).reverse();
          const changed = history.some(log => trainingProtocolKey(log.protocol) !== trainingProtocolKey(ex));
          return <article key={ex.id} className="physical-training-exercise-card">
          <div className="taf-card-head"><div><span className="taf-card-eyebrow">TESTE DO EDITAL</span><h3>{ex.icon} {ex.name}</h3><span className="taf-card-requirement">{formatTrainingRequirement(ex)}</span>{ex.instructions && <p>{ex.instructions}</p>}</div></div>
          <div className="taf-last-performance"><div><span>Último desempenho</span><strong>{last ? formatTrainingResult(last.result_value, ex.test_type) : 'Ainda sem registro'}</strong>{last && ex.test_type === 'distance_fixed_time' && <small>em {formatTrainingTime(last.elapsed_seconds || ex.test_duration_seconds)}</small>}</div>{last && <time>{dateLabel(last.performed_at)}</time>}</div>
          <p className={`taf-outcome taf-outcome-${outcome.status}`}>{outcome.label}</p>
          <div className="stat-bar-track"><div className={`stat-bar-fill ${outcome.status === 'achieved' ? 'high' : 'medium'}`} style={{
                width: `${percent}%`
              }} /></div>
          <button className="taf-history-toggle" type="button" aria-expanded={expanded === ex.id} onClick={() => setExpanded(expanded === ex.id ? null : ex.id)}>{expanded === ex.id ? 'Ocultar histórico' : 'Ver histórico e evolução'} ({history.length})</button>
          {expanded === ex.id && <div className="physical-training-exercise-history">
            <p>O gráfico compara apenas testes completos com a mesma exigência atual. {changed ? 'Registros de exigências anteriores estão identificados abaixo.' : ''}</p>
            {graph.length > 0 && <ResponsiveContainer width="100%" height={200}><BarChart data={graph}><CartesianGrid stroke="var(--border-color)" /><XAxis dataKey="performed_at" tickFormatter={dateLabel} /><YAxis /><Tooltip formatter={value => ex.test_type === 'distance_fixed_time' ? formatTrainingTime(value) : formatTrainingResult(value, ex.test_type)} labelFormatter={dateLabel} /><ReferenceLine y={Number(ex.test_type === 'distance_fixed_time' ? ex.test_duration_seconds : ex.minimum_result)} stroke="var(--accent-color)" strokeDasharray="4 4" /><Bar dataKey={ex.test_type === 'distance_fixed_time' ? 'elapsed_seconds' : 'result_value'} name={ex.test_type === 'distance_fixed_time' ? 'Tempo gasto' : 'Desempenho'} fill="var(--primary-color)" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>}
            {!history.length && <p>Registre seu primeiro desempenho.</p>}
            <div className="physical-training-log-list">{history.map(log => <div className="taf-history-row" key={log.id}>
              <strong>{dateLabel(log.performed_at)} · {formatTrainingResult(log.result_value, log.protocol.test_type)}</strong><span>{evaluateTrainingGoal(log, ex).label}</span>
              <small>{log.protocol.test_type === 'distance_fixed_time' && log.elapsed_seconds ? `Tempo gasto: ${formatTrainingTime(log.elapsed_seconds)} · ` : ''}Meta do registro: {formatTrainingRequirement(log.protocol)}</small>
              {!log.is_complete_test && <small>Tempo realizado: {formatTrainingTime(log.elapsed_seconds)}</small>}
              {log.notes && <p>{log.notes}</p>}
              <div className="taf-history-actions"><button className="taf-history-toggle" type="button" onClick={() => setModal({
                      exerciseId: ex.id,
                      log
                    })}>Editar registro</button><button className="physical-training-log-delete" type="button" aria-label="Excluir registro" onClick={() => remove(log.id)}><DeleteOutlineOutlined /></button></div>
            </div>)}</div>
          </div>}
        </article>;
        })}</div>
      {logs.some(log => !exercises.some(ex => ex.id === log.exercise_id)) && <details className="taf-archived"><summary>Histórico de exercícios desabilitados ou de outro público</summary>{logs.filter(log => !exercises.some(ex => ex.id === log.exercise_id)).map(log => <div key={log.id} className="taf-history-row"><strong>{log.protocol.name} · {dateLabel(log.performed_at)}</strong><span>{formatTrainingResult(log.result_value, log.protocol.test_type)} · {evaluateTrainingGoal(log).label}</span><small>{formatTrainingRequirement(log.protocol)}</small><button type="button" className="physical-training-log-delete" onClick={() => remove(log.id)} aria-label="Excluir registro"><DeleteOutlineOutlined /></button></div>)}</details>}
    </>}
    {modal && <PhysicalTrainingLogModal exercises={modal.log ? [{
      ...modal.log.protocol,
      id: modal.log.exercise_id
    }] : exercises} defaultExerciseId={modal.exerciseId} existingLog={modal.log} onSave={save} onClose={() => setModal(null)} />}
    {paywall && <PlansModal onClose={() => setPaywall(false)} heroTitle="Continue acompanhando seus treinos" trackingPrefix="training" />}
  </section>;
}
